import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, map, tap, catchError, of, shareReplay, finalize } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { LoginRequest, LoginResponse, TokenResponse, CurrentUserResponse } from '../../../../../shared/src/types/auth';
import { isStaffUser, resolveLanding } from './landing-policy';
import { TwoStepChallenge, isTwoStepChallenge } from './two-step';

export type LogoutReason = 'logout' | 'expired' | 'session-expired' | 'idle-timeout';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apiUrl = environment.apiBaseUrl;
  private readonly websiteUrl = (environment as any).websiteUrl || 'https://www.ringoffice.com';

  private readonly accessToken$ = new BehaviorSubject<string | null>(null);
  private readonly currentUser$ = new BehaviorSubject<CurrentUserResponse | null>(null);

  readonly isAuthenticated$: Observable<boolean> = this.accessToken$.pipe(
    map(token => token !== null),
  );

  readonly user$: Observable<CurrentUserResponse | null> = this.currentUser$.asObservable();

  constructor() {
    // Keep signal in sync with BehaviorSubject
    this.currentUser$.subscribe(u => this._currentUser.set(u));
  }

  /**
   * Password sign-in. With email two-step verification on (Owais, 27 Oct 2026) OneGate answers 202 with a challenge and
   * no session; the caller then sends the emailed code to verifyLoginCode(). Nothing is stored until a session exists.
   */
  login(email: string, password: string): Observable<LoginResponse | TwoStepChallenge> {
    const body: LoginRequest = { email, password };
    return this.http
      .post<LoginResponse | TwoStepChallenge>(`${this.apiUrl}/auth/login`, body, { withCredentials: true })
      .pipe(tap(response => this.acceptLogin(response)));
  }

  verifyLoginCode(challengeId: string, code: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/login/two-step`, { challengeId, code }, { withCredentials: true })
      .pipe(tap(response => this.acceptLogin(response)));
  }

  /** Authenticator challenge only (Owais, 30 Oct 2026): a single-use recovery code instead of the app code. */
  verifyLoginRecoveryCode(challengeId: string, recoveryCode: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/login/two-step`, { challengeId, recoveryCode }, { withCredentials: true })
      .pipe(tap(response => this.acceptLogin(response)));
  }

  resendLoginCode(challengeId: string): Observable<TwoStepChallenge> {
    return this.http.post<TwoStepChallenge>(`${this.apiUrl}/auth/login/two-step/resend`, { challengeId }, { withCredentials: true });
  }

  private acceptLogin(response: LoginResponse | TwoStepChallenge) {
    if (isTwoStepChallenge(response) || !(response as LoginResponse)?.accessToken) return;
    this.accessToken$.next((response as LoginResponse).accessToken);
    this.currentUser$.next((response as LoginResponse).user);
  }

  // Owais, 27 Oct 2026: one refresh at a time. Several requests that find the access token expired together share the same
  // refresh instead of each using the same one-time refresh cookie (which signed people out after a short break).
  private refreshInFlight: Observable<TokenResponse | null> | null = null;
  /** Why the last refresh failed ('SessionIdle' = 15 minutes without activity after 12 hours). */
  private lastRefreshError: string | null = null;

  refresh(options: { redirectOnFailure?: boolean } = {}): Observable<TokenResponse | null> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.http
        .post<TokenResponse>(`${this.apiUrl}/auth/refresh`, {}, { withCredentials: true })
        .pipe(
          tap(response => { this.lastRefreshError = null; this.accessToken$.next(response.accessToken); }),
          catchError((e) => { this.lastRefreshError = e?.error?.error || 'Unauthorized'; return of(null); }),
          finalize(() => { this.refreshInFlight = null; }),
          shareReplay(1),
        );
    }
    return this.refreshInFlight.pipe(
      tap(response => {
        if (response) return;
        if (options.redirectOnFailure === false) this.clearLocalSession();
        else this.handleSessionExpired();
      }),
    );
  }

  /** Start of the sign-in session (ms), from the access token; null when unknown. Used for the 12-hour rule on screen. */
  sessionStartedAt(): number | null {
    const token = this.accessToken$.value;
    if (!token) return null;
    try {
      const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(atob(part.padEnd(part.length + (4 - part.length % 4) % 4, '=')));
      return Number.isFinite(claims?.sat) ? claims.sat * 1000 : null;
    } catch { return null; }
  }

  /** Reports genuine user activity for this session (the app calls it at most once a minute). */
  reportActivity(): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/auth/activity`, {}, { withCredentials: true }).pipe(catchError(() => of(null)));
  }

  logout(reason: LogoutReason = 'logout'): Observable<void> {
    return this.http
      .post<void>(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .pipe(
        tap(() => this.clearSession(reason)),
        catchError(() => {
          this.clearSession(reason);
          return of(undefined);
        }),
      );
  }

  me(): Observable<CurrentUserResponse> {
    return this.http
      .get<CurrentUserResponse>(`${this.apiUrl}/auth/me`, { withCredentials: true })
      .pipe(
        tap(user => this.currentUser$.next(user)),
      );
  }

  // Signal-style accessors for use in templates
  private readonly _currentUser = signal<CurrentUserResponse | null>(null);
  readonly currentUser = this._currentUser.asReadonly();

  getAccessToken(): string | null {
    return this.accessToken$.getValue();
  }

  getCurrentUser(): CurrentUserResponse | null {
    return this.currentUser$.getValue();
  }

  isStaff(): boolean {
    return isStaffUser(this.getCurrentUser());
  }

  landingUrl(requested?: string | null): string {
    return resolveLanding(this.getCurrentUser(), requested);
  }

  isCustomerAccountRoute(url?: string): boolean {
    if (typeof window === 'undefined') return false;
    const current = url || `${window.location.pathname}${window.location.search}`;
    try {
      const parsed = new URL(current, window.location.origin);
      return parsed.pathname === '/my-account' || parsed.pathname.startsWith('/my-account/');
    } catch {
      return false;
    }
  }

  redirectCustomerSessionExpired(nextUrl?: string, reason: LogoutReason = 'session-expired'): void {
    this.clearLocalSession();
    if (this.inPopupFlow()) return;
    if (typeof window !== 'undefined') {
      window.location.replace(this.customerSessionExpiredUrl(nextUrl, reason));
    }
  }

  handleSessionExpired(nextUrl?: string, reason: LogoutReason = 'session-expired'): void {
    if (this.lastRefreshError === 'SessionIdle') reason = 'idle-timeout';
    this.clearLocalSession();
    if (this.inPopupFlow()) return;
    if (this.isCustomerAccountRoute(nextUrl)) {
      if (typeof window !== 'undefined') {
        window.location.replace(this.customerSessionExpiredUrl(nextUrl, reason));
      }
      return;
    }
    this.router.navigate(['/welcome'], { queryParams: this.reasonQuery(reason) });
  }

  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser() as any;
    if (!user) return false;

    // PREFERRED PATH — backend's /auth/me resolves effective permissions
    // (direct bindings + group-inherited + platform.admin wildcard expansion)
    // and ships them as a top-level `permissions` array. This is the source
    // of truth in the group-based RBAC model where users no longer have
    // direct role bindings.
    if (Array.isArray(user.permissions)) {
      if (
        user.permissions.includes('*') ||
        user.permissions.includes('platform.admin') ||
        user.permissions.includes(permission)
      ) {
        return true;
      }
      // If the resolved array exists but doesn't grant access, that's the answer.
      // Don't fall through to the legacy direct-roles check (which would be stale).
      return false;
    }

    // LEGACY FALLBACK — old clients without resolved permissions.
    if (user.roles) {
      return user.roles.some((role: any) =>
        role.permissions?.includes(permission) ||
        role.permissions?.includes('platform.admin') ||
        role.permissions?.includes('*'),
      );
    }
    return false;
  }

  /**
   * Set session from an already-issued access token (e.g. Microsoft OAuth callback).
   * Loads /auth/me to populate currentUser.
   */
  setAccessTokenAndLoadUser(accessToken: string): Observable<CurrentUserResponse> {
    this.accessToken$.next(accessToken);
    return this.me();
  }

  private clearLocalSession(): void {
    this.accessToken$.next(null);
    this.currentUser$.next(null);
  }

  private clearSession(reason: LogoutReason = 'logout'): void {
    this.clearLocalSession();
    // Don't kick the user out to /welcome when we're inside an SSO popup
    // (URL has popup=1) or on the /sso handoff route — the popup has its
    // own state machine (msPrompt → MS button → mint → postMessage → close)
    // and any forced /welcome redirect would break the customer/employee
    // selector showing up inside the popup window.
    if (this.inPopupFlow()) return;
    if (this.isCustomerAccountRoute()) {
      if (typeof window !== 'undefined') {
        if (reason !== 'logout') {
          window.location.replace(this.customerSessionExpiredUrl(undefined, reason));
        } else {
          window.location.replace(this.websiteUrl);
        }
      }
      return;
    }
    this.router.navigate(['/welcome'], { queryParams: this.reasonQuery(reason) });
  }

  private inPopupFlow(): boolean {
    return typeof window !== 'undefined' &&
      (window.location.search.includes('popup=1') ||
       window.location.pathname.startsWith('/sso'));
  }

  /**
   * Owais, 27 Oct 2026: no My Account session here -> the website's /api/onegate/continue. With a valid website session the customer
   * comes straight back (no website page, no sign-in popup); without one the website sign-in page opens with this destination.
   */
  private customerSessionExpiredUrl(nextUrl?: string, reason: LogoutReason = 'session-expired'): string {
    const target = new URL('/api/onegate/continue', this.websiteUrl);
    target.searchParams.set('reason', reason === 'idle-timeout' ? 'idle-timeout' : 'session-expired');
    target.searchParams.set('next', this.safeCustomerNext(nextUrl));
    return target.toString();
  }

  private reasonQuery(reason: LogoutReason): Record<string, string> | undefined {
    if (reason === 'idle-timeout') return { reason: 'idle-timeout' };
    if (reason === 'expired' || reason === 'session-expired') return { reason: 'session-expired' };
    return undefined;
  }

  private safeCustomerNext(nextUrl?: string): string {
    if (typeof window === 'undefined') return '/my-account';
    try {
      const source = nextUrl || `${window.location.pathname}${window.location.search}`;
      const parsed = new URL(source, window.location.origin);
      const next = `${parsed.pathname}${parsed.search}`;
      return next === '/my-account' || next.startsWith('/my-account?') || next.startsWith('/my-account/')
        ? next
        : '/my-account';
    } catch {
      return '/my-account';
    }
  }
}
